// this code was pulled out of old service manager

import Logger from "../../clients/logger";
import { System } from "../../common/system";
import SpawnUtils from "../window/Common/spawnUtils";
import systemManagerClient from "../../common/systemManagerClient";
import { StandardPromise } from "../../types";
import _get from "lodash/get";
import merge from "deepmerge";

type Manifest = Record<string, any>;
type FinsembleConfig = Record<string, any>;
type ServiceConfig = Record<string, any>;

type WorkerConfig = {
	name: string;
	[key: string]: any;
};

/**
 * Launcher services in various config-driven forms
 */
export class ServiceLauncher {
	runningServices: object = {};

	manifest: Manifest;

	workerTop: number = 100;

	workerLeft: number = 100;

	webWorkers: object = {};

	shutdownManager: any;

	finsembleConfig: FinsembleConfig;

	constructor(params) {
		this.manifest = params.manifest;
		this.finsembleConfig = params.manifest.finsemble;
		this.shutdownManager = params.shutdownManager;
	}

	/**
	 * Invoked by boot task "updateServiceLauncherConfigTask".
	 * Updates this.finsembleConfigs to make sure we have
	 * SecurityPolicies in finsembleConfig object to be used later on in
	 * this.composeWorkerConfig to get security policy and permissions.
	 * @param configs Finsemble configs
	 */
	public updateConfig(configs: Record<string, any>) {
		this.finsembleConfig = configs;
	}

	public updateManifest(manifest: Manifest) {
		this.manifest = manifest;
	}

	/**
	 * Returns the config for the given service. This is used only for *core* services: router, config, dataStore, logger.
	 * Core services are those that come up before the config system is initialized.
	 *
	 * This function will combine configs from requiredServices, services, and servicesConfig entries.
	 * @param {string} serviceName The name of the service
	 * @returns {any} The config entry for the service or an empty object if the configuration isn't found.
	 */
	public getServiceConfig(serviceName: string): ServiceConfig {
		let rawServiceName = serviceName;
		// Normalize the rawServiceName by removing "Service", for instance from "routerService"
		const suffixPosition = rawServiceName.indexOf("Service");
		if (suffixPosition !== -1) rawServiceName = rawServiceName.substring(0, suffixPosition);

		const fullServiceName = `${rawServiceName}Service`;

		// Service configs can be found in four separate config files across finsemble and finsemble-seed
		// They are loaded in two stages: boot and core. At the boot stage, only the built in configs and the manifest are available

		// Get the default config for this service, from finsemble.requiredServicesConfig
		// This can be found in finsemble/configs/config.json
		//
		// Next, if we're at the stage where "services" configuration is loaded, use those values to override anything from requiredServices.
		// This can be found in finsemble/configs/services.json
		//
		// Finally, if there is a finsemble.servicesConfig entry then copy the values over our default.
		// These would be developer overrides of the finsemble defaults, from finsemble-seed configs
		// For core services (router, config, logging, dataStore) these entries can be added in finsemble-seed/configs/application/manifest-local.json
		// For other services these entries can be added to finsemble-seed/configs/config.json
		return this.mergeConfig([
			{ path: "system.requiredServicesConfig", entry: fullServiceName },
			{ path: "system.requiredServicesConfig", entry: rawServiceName },
			{ path: "services", entry: fullServiceName },
			{ path: "services", entry: rawServiceName },
			{ path: "servicesConfig", entry: fullServiceName },
			{ path: "servicesConfig", entry: rawServiceName },
		]);
	}

	/**
	 * Creates a service, either as a script, window or process.
	 */
	/**
	 * Creates service
	 * @param worker
	 * @returns promisified error if failed to launch
	 */
	public async createService(worker: ServiceConfig): StandardPromise<void> {
		// Makes certain that a service isn't started more than once. This can happen because of the multiple entry points for services (requiredServicesConfig, services and servicesConfig).
		if (this.runningServices[worker.name]) return {};
		this.runningServices[worker.name] = true;
		const updatedWorker = {
			...this.getServiceConfig(worker.name),
		};
		const workerConfig = this.composeWorkerConfig(Object.assign({}, worker, updatedWorker));

		if (!workerConfig.name) {
			let errorMessage = `Tried to create a service without a name. Check your config.`;
			if (worker && updatedWorker) {
				errorMessage = `${errorMessage}
				config: ${JSON.stringify(worker, null, 4)}
				updatedConfig: ${JSON.stringify(updatedWorker, null, 4)}`;
			}
			systemManagerClient.systemLog({ error: true }, errorMessage);
			return { err: errorMessage };
		}

		const defaultSpawnAs = workerConfig.isolate ? "process" : "window";
		const { spawnAs = defaultSpawnAs } = workerConfig;

		Logger.console.debug(`serviceLauncher: createService() ${worker.name}`, spawnAs, workerConfig);

		let result: any;
		switch (spawnAs) {
			case "window":
			default:
				result = await this.launchServiceAsWindow(updatedWorker, workerConfig);
				break;
			// backwards compatibility for removal of windowType application
			case "process":
				//We could have two entry points for "apps", affinity disabled and process for services, otherwise all services will need to set affinity=false
				workerConfig.affinity = false;
				result = await this.launchServiceAsWindow(updatedWorker, workerConfig);
				break;
			case "script":
				result = await this.launchServiceAsScript(updatedWorker);
				break;
		}

		Logger.console.debug(`serviceLauncher: createService() ${worker.name} successfully spawned`);

		// So that windows are staggered when visible.
		this.workerTop = this.workerTop + 25;
		this.workerLeft = this.workerLeft + 25;
		return result;
	}

	// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
	// The remaining methods are private
	// /////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

	/**
	 * Helper function to search through the config for a list of path,entry tuples and merge them all together.
	 * Use this when you need a series of configs to override one another but with the results merged together.
	 *
	 * @param {array} arr Array of path,entry tuples
	 * @returns {any} Returns a config object, or an empty object if no config is found.
	 */
	private mergeConfig(arr: { path: string; entry: string }[]): Record<string, any> {
		let config = {};

		arr.forEach((element) => {
			// Use lodash to get an object by path, or return empty string if no such object
			// Then use deepmerge to merge into the container object
			config = merge(config, _get(this.finsembleConfig, `${element.path}.${element.entry}`, {}));
		});
		return config;
	}

	// Launches a service as a <script> tag that is injected into serviceManager.html
	// The promise resolves when the script has been addwed (not when the service is online)
	private launchServiceAsScript(worker: ServiceConfig): StandardPromise<void> {
		return new Promise((resolve) => {
			const url = worker.file;
			const script = document.createElement("script");
			script.setAttribute("src", url);
			document.head.appendChild(script);
			resolve({});
		});
	}

	// Launches a service as a window. This will make it a child window off of serviceManager.html (following "affinity" rules like any other window)
	// The promise resolves when the window is launched (not when the service is online)
	private launchServiceAsWindow(worker: ServiceConfig, workerConfig: WorkerConfig): StandardPromise<void> {
		return new Promise((resolve) => {
			System.Window.createNew(
				{
					...workerConfig,
					windowName: workerConfig.name,
					affinity: workerConfig.affinity,
				},
				(err) => {
					if (err) {
						resolve({ err });
						return;
					}
					resolve({});
				}
			);
		});
	}

	private composeWorkerConfig(worker: ServiceConfig): WorkerConfig {
		const customData = merge(worker, { manifest: this.manifest });
		// globalAffinity will fallback to the default set in the schema but not for the first four services.
		// They still need a manual fallback since the config won't be available yet.
		const globalAffinity = this.finsembleConfig?.servicesConfig?.launcher?.defaultServiceAffinity ?? null;
		if (worker.affinity === true) {
			Logger.system.error(
				"An invalid affinity setting: true was passed in an application config. Affinity values must be strings or false"
			);
			delete worker.affinity;
		}
		const defaultWD = {
			url: worker.external ? worker.html : worker.html || "base.html",
			name: worker.name,
			defaultTop: this.workerTop,
			defaultLeft: this.workerLeft,
			autoShow: worker.visible,
			frame: worker.frame,
			customData,
			alwaysOnTop: false,
			fixedPosition: false,
			saveWindowState: false,
			cornerRounding: true,
			waitForPageLoad: true,
			isolate: worker.isolate,
			spawnAs: worker.spawnAs,
			affinity: worker.affinity ?? globalAffinity ?? "Finsemble",
			securityPolicy: worker.securityPolicy || null,
			permissions: worker.permissions || {},
			icon: this.manifest.startup_app ? this.manifest.startup_app.applicationIcon : null,
		};

		const descriptor = merge(defaultWD, worker.window || {});
		// If the required services aren't up, we can't actually look up security policies. The required services should all have unfettered permissions.
		if (this.finsembleConfig.securityPolicies) {
			descriptor.securityPolicy = SpawnUtils.getSecurityPolicy(descriptor, this.finsembleConfig);
			descriptor.permissions = SpawnUtils.getPermissions(descriptor, this.finsembleConfig);
		}

		return descriptor;
	}
}
